import type {
  DealApi,
  DealFormData,
  DealFormDataWithTargeting,
  DealFormState,
  DealState,
  ListDealBase,
} from '@openx/types/deal.ts';
import type { DealTroubleshootingTimeResolutionEnum } from '@openx/types/gqlTypes.ts';
import type { WithRequired } from '@openx/types/helpers.ts';
import type { MonetizingApi } from '@openx/types/monetizing.ts';
import type { OptionStateMap, OptionsMap } from '@openx/types/options.ts';
import type { OrganizationOptionData } from '@openx/types/organization.ts';
import type { Package } from '@openx/types/package.ts';
import type { UseGetContextualPricing } from '@openx/types/targeting/targetingHookTypes.ts';
import type { DealGroupInfo } from '@openx/utils/state/deals/dealsList/api.ts';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { NavigateFunction } from 'react-router';
import type {
  CurrenciesFormFetchResp,
  DealAccount,
  DealFetchRes,
  DealsListStoreProps,
  DealsRowsActionsType,
  DealsTableType,
  IUseBrandsFetch,
  IUseBuyersFetch,
  IUseOpenAuctionsFetch,
  IUseParticipantFunctions,
  IUseParticipantHooks,
  IUsePmpDealTypeFetch,
  IUseRelatedAccountsFetch,
  MultipleDealsFetch,
  OnBehalfType,
  OpenAuctionsAccessFetchResp,
  TroubleshootingType,
  UseAccountOptionsFetch,
  UseAgenciesOptionsFetch,
  UseAmazonSyncStatusOptionsFetch,
  UseBrandsOptionsFetch,
  UseBuyerOrganizationsFetch,
  UseDealDiscounts,
  UseDealForecast,
  UseDealForecastActions,
  UseDealForecastState,
  UseDealFormSelector,
  UseDealFormSubmit,
  UseDealGroupFetch,
  UseDealHealthScoreRootCauseDetails,
  UseDealInsightsFilterOptions,
  UseDealInsightsJobApi,
  UseDealOperations,
  UseDealRboInfoFetch,
  UseDealRevenueBreakdownFetch,
  UseDealSourceFetch,
  UseDealsLandingListFetchOptions,
  UseDealsTableFetchArgs,
  UseDealTroubleshootingAgentMessageMutationState,
  UseDealTroubleshootingAgentSendMessage,
  UseDealTroubleshootingAgentSessionHistory,
  UseDealTroubleshootingAgentSessionList,
  UseDealTroubleshootingFeedback,
  UseDealTroubleshootingJobFetch,
  UseDirectDealsPartnersFetchHook,
  UseExpectedAdCategoryFetch,
  UseFeePublisherPmpFetch,
  UseFetchDealIdGroupInfoParams,
  UseFetchDealInsights,
  UseGetDealInsightsJobId,
  UseGlobalFeePublisherPmpFetch,
  UseOrganizationsByIdsFetch,
  UsePackageFetch,
  UsePackageLazyFetch,
  UsePdpSyncDealStatusOptionsFetch,
  UsePdpSyncNegotiationStatusOptionsFetch,
  UsePdpSyncStatusOptionsFetch,
  UsePostAuctionDiscountsFetch,
  UseRebatesFetch,
  UseRefreshAuthToken,
  UseSetMultiproviderEnabledDataPartner,
  UseThirdPartyPartnersFetchHook,
  UseTpfBoostedBidPriceFetch,
  UseValidationSchemaHook,
} from './DealHookTypes.ts';

export type DealFormActions<
  T extends DealFormData = DealFormData,
  D extends Record<string, unknown> = Record<string, unknown>,
> = {
  dealId: string | undefined;
  deal: D;
  fallbackValues?: T;
  isDsp?: boolean;
  isPacingEnabled?: boolean;
  navigate: NavigateFunction;
  /** Called once the deal has been saved successfully (currently implemented by OA only). */
  onSuccess?: () => void;
};
export interface DealProviderConfig {
  isOa?: boolean;
  onBehalfOrganization?: OrganizationOptionData | null;
  setOnBehalfOrganization?: (selectedOrg: OrganizationOptionData | null) => void;
  useAccountOptionsFetch: UseAccountOptionsFetch;
  //TODO: UIG-540 Review this type definition ids: (string | null)[]??
  useBrandsFetch?: IUseBrandsFetch;
  useBuyersFetch: IUseBuyersFetch;
  useAgenciesOptionsFetch: UseAgenciesOptionsFetch;
  useBrandsOptionsFetch: UseBrandsOptionsFetch;
  useCurrenciesFetch: () => OptionStateMap;
  useCurrenciesFormFetch: () => CurrenciesFormFetchResp;
  useDealFetch: <T extends DealApi>(
    dealUid: string,
    options?: { withPackage?: 'whenMissing' | 'always' }
  ) => DealFetchRes<T>;

  useDealsFetch: <T extends ListDealBase>(queryCrit) => DealsListStoreProps<T>;
  useDealFormSelector: UseDealFormSelector;
  useDealFormActions: <
    T extends DealFormData = DealFormData,
    D extends Record<string, unknown> = Record<string, unknown>,
  >() => {
    skipDealUpdate: (dealUid: string) => void;
    getDealFormData: (id: string | undefined) => void;
    submitDealForm: ({
      dealId,
      deal,
      fallbackValues,
      isDsp,
      isPacingEnabled,
      navigate,
    }: DealFormActions<T, D>) => Promise<void> | void;
  };
  useDealsListFetch: <T extends ListDealBase>(queryCrit) => DealsListStoreProps<T>;
  useDealsLandingListFetch: <T extends ListDealBase>(
    queryCrit,
    options?: UseDealsLandingListFetchOptions
  ) => DealsListStoreProps<T>;
  useFetchDealIdGroupInfo: (args: UseFetchDealIdGroupInfoParams) => {
    dealGroupInfo: DealGroupInfo[];
  };
  useDealRboInfoFetch: UseDealRboInfoFetch;
  useDealsTableFetch: <T extends ListDealBase, O extends Record<string, unknown>>(
    dealAccounts?: DealAccount[],
    options?: O,
    args?: UseDealsTableFetchArgs
  ) => DealsTableType<T>;
  useDealsRowActions: DealsRowsActionsType;
  useDemandPartnersFetch: (filters?: {
    account_uid?: string | null;
    enabled?: boolean;
  }) => WithRequired<OptionStateMap, 'refetch'>;
  useOnBehalfOfAccountId: () => string;
  useOnBehalfOfFetch: () => OnBehalfType;
  useOpenAuctionsAccessFetch: () => OpenAuctionsAccessFetchResp;
  useOpenAuctionsFetch?: IUseOpenAuctionsFetch;
  usePackageFetch: UsePackageFetch;
  useParticipantFunctions?: IUseParticipantFunctions;
  useParticipantHooks: IUseParticipantHooks;
  usePmpDealTypeFetch?: IUsePmpDealTypeFetch;
  useRelatedAccountsFetch?: IUseRelatedAccountsFetch;
  useThirdPartyPartnersFetch: UseThirdPartyPartnersFetchHook;
  useDirectDealsPartnersFetch: UseDirectDealsPartnersFetchHook;
  useTroubleshootingFetch: (
    dealUid: string,
    params?: {
      lookbackHours?: number;
      startDate?: string;
      endDate?: string;
      dspId?: string;
      resolution?: DealTroubleshootingTimeResolutionEnum;
    }
  ) => TroubleshootingType;
  useValidationSchema: UseValidationSchemaHook;
  useCanEditSegmentPartnerPlatformShare: () => boolean;
  useContentAttributesFetch: () => {
    isContentAttributesLoading: boolean;
    contentAttributes: OptionsMap;
    refetchContentAttributes: () => Promise<unknown>;
  };
  useCreativeTypesFetch: () => {
    isCreativeTypesLoading: boolean;
    creativeTypes: OptionsMap;
    refetchCreativeTypes: () => Promise<unknown>;
  };
  useLanguagesFetch: () => {
    isLanguagesLoading: boolean;
    languages: OptionsMap;
    refetchLanguages: () => Promise<unknown>;
  };
  useMarketBrandGroupsFetch: () => {
    marketBrandGroupsLoading: boolean;
    marketBrandGroups: OptionsMap;
    refetchMarketBrandGroups: () => Promise<unknown>;
  };
  useMonetizingFetch: (
    monetizingUid?: string | null,
    isAsTemplate?: boolean
  ) => {
    isLoading: boolean;
    monetizing?: MonetizingApi;
  };
  useInitialValues: (
    dealForm: DealFormState,
    defaultAccountUid: string | null,
    pckg?: Package,
    enforcedUseAs?: 'template' | 'targeting'
  ) => DealFormDataWithTargeting;
  useInitialValuesLazy: (
    dealForm: DealFormState<DealState>,
    defaultAccountUid: string | null,
    pckg?: Package | null,
    enforcedUseAs?: 'template' | 'targeting'
  ) => () => DealFormDataWithTargeting;
  useDealForecast: UseDealForecast;
  useDealForecastActions: UseDealForecastActions;
  useGetForecastJobId: (DV360_DEMAND_PARTNER_ID: string) => { isLoading: boolean; postForecast };
  useFetchDealForecast: (jobId: string) => void;
  useDealOperations: UseDealOperations;
  useDealForecastState?: UseDealForecastState;
  useMultipleDealsFetch: MultipleDealsFetch;
  useExpectedAdCategoryFetch: UseExpectedAdCategoryFetch;
  useDealHealthScoreRootCauseDetails: UseDealHealthScoreRootCauseDetails;
  useDealTroubleshootingJobFetch: UseDealTroubleshootingJobFetch;
  useDealTroubleshootingFeedback: UseDealTroubleshootingFeedback;
  useDealTroubleshootingAgentSessionList: UseDealTroubleshootingAgentSessionList;
  useDealTroubleshootingAgentSessionHistory: UseDealTroubleshootingAgentSessionHistory;
  useDealTroubleshootingAgentSendMessage: UseDealTroubleshootingAgentSendMessage;
  useDealTroubleshootingAgentMessageMutationState: UseDealTroubleshootingAgentMessageMutationState;
  useRefreshAuthToken: UseRefreshAuthToken;
  useDealGroupFetch: UseDealGroupFetch;
  useDealSourceFetch: UseDealSourceFetch;
  useDealInsightsFilterOptions: UseDealInsightsFilterOptions;
  useGetDealInsightsJobId: UseGetDealInsightsJobId;
  useFetchDealInsights: UseFetchDealInsights;
  useDealInsightsJobApi: UseDealInsightsJobApi;
  useDealDiscounts: UseDealDiscounts;
  usePackageLazyFetch: UsePackageLazyFetch;
  useSetMultiproviderEnabledDataPartner: UseSetMultiproviderEnabledDataPartner;
  /** Optional: only apps with contextual segments (oa-ui) provide it. */
  useGetContextualPricing?: UseGetContextualPricing;
  useDealFormSubmit: UseDealFormSubmit;
  useOrganizationsByIdsFetch: UseOrganizationsByIdsFetch;
  usePdpSyncStatusOptionsFetch: UsePdpSyncStatusOptionsFetch;
  usePdpSyncDealStatusOptionsFetch: UsePdpSyncDealStatusOptionsFetch;
  usePdpSyncNegotiationStatusOptionsFetch: UsePdpSyncNegotiationStatusOptionsFetch;
  useAmazonSyncStatusOptionsFetch: UseAmazonSyncStatusOptionsFetch;
  useFeePublisherPmpFetch: UseFeePublisherPmpFetch;
  usePostAuctionDiscountsFetch: UsePostAuctionDiscountsFetch;
  useRebatesFetch: UseRebatesFetch;
  useDealRevenueBreakdownFetch: UseDealRevenueBreakdownFetch;
  useGlobalFeePublisherPmpFetch: UseGlobalFeePublisherPmpFetch;
  useTpfBoostedBidPriceFetch: UseTpfBoostedBidPriceFetch;
  useBuyerOrganizationsFetch: UseBuyerOrganizationsFetch;
}

export interface DealProviderProps {
  children: ReactNode;
  dealConfig: DealProviderConfig;
}

export type DealContext = DealProviderConfig;

const dealContext = createContext<DealContext>({
  isOa: false,
  onBehalfOrganization: null,
  setOnBehalfOrganization: () => null,
  useAccountOptionsFetch: () => {
    throw new Error('useAccountOptionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useAgenciesOptionsFetch: () => {
    throw new Error('useAgenciesOptionsFetch" Not implemented. Ensure you are inside the DealContext');
  },
  useBrandsOptionsFetch: () => {
    throw new Error('useBrandsOptionsFetch" Not implemented. Ensure you are inside the DealContext');
  },
  useBuyersFetch: () => {
    throw new Error('useBuyersFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useContentAttributesFetch: () => {
    throw new Error('useContentAttributesFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useCreativeTypesFetch: () => {
    throw new Error('useCreativeTypesFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useCurrenciesFetch: () => {
    throw new Error('useCurrenciesFetch" Not implemented. Ensure you are inside the DealContext');
  },
  useCurrenciesFormFetch: () => {
    throw new Error('useCurrenciesFormFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealDiscounts: () => {
    throw new Error('useDealDiscounts: Not implemented. Ensure you are inside the DealContext');
  },
  useDealFetch: () => {
    throw new Error('useDealFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealForecast: () => {
    throw new Error('useDealForecast: Not implemented. Ensure you are inside the DealContext');
  },
  useDealForecastActions: () => {
    throw new Error('useDealForecastActions: Not implemented. Ensure you are inside the DealContext');
  },
  useDealFormActions: () => {
    throw new Error('"useDealFormActions Function not implemented.');
  },
  useDealFormSelector: () => {
    throw new Error('useDealFormSelector: Function not implemented.');
  },
  useDealFormSubmit: () => {
    throw new Error('useDealFormSubmit: Not implemented. Ensure you are inside the DealContext');
  },
  useDealGroupFetch: () => {
    throw new Error('useDealGroupFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealInsightsFilterOptions: () => {
    throw new Error('useDealInsightsFilterOptions: Not implemented. Ensure you are inside the DealContext');
  },
  useDealOperations: () => {
    throw new Error('useDealOperations: Not implemented. Ensure you are inside the DealContext');
  },
  useDealRboInfoFetch: () => {
    throw new Error('useDealRboInfoFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealSourceFetch: () => {
    throw new Error('useDealSourceFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealsFetch: () => {
    throw new Error('useDealsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealsListFetch: () => {
    throw new Error('useDealsListFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealsLandingListFetch: () => {
    throw new Error('useDealsLandingListFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealsRowActions: () => {
    throw new Error('useDealsRowActions: Not implemented. Ensure you are inside the DealContext');
  },
  useDealsTableFetch: () => {
    throw new Error('useDealsTableFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDemandPartnersFetch: () => {
    throw new Error('useDemandPartnersFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useExpectedAdCategoryFetch: () => {
    throw new Error('useExpectedAdCategoryFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealHealthScoreRootCauseDetails: () => {
    throw new Error('useDealHealthScoreRootCauseDetails: Not implemented. Ensure you are inside the DealContext');
  },
  useFetchDealForecast: () => {
    throw new Error('useFetchDealForecast: Not implemented. Ensure you are inside the DealContext');
  },
  useFetchDealIdGroupInfo: () => {
    throw new Error('useFetchDealIdGroupInfo: Not implemented. Ensure you are inside the DealContext');
  },
  useFetchDealInsights: () => {
    throw new Error('useFetchDealInsights: Not implemented. Ensure you are inside the DealContext');
  },
  useDealInsightsJobApi: () => {
    throw new Error('useDealInsightsJobApi: Not implemented. Ensure you are inside the DealContext');
  },
  useDealTroubleshootingJobFetch: () => {
    throw new Error('useDealTroubleshootingJobFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDealTroubleshootingFeedback: () => {
    throw new Error('useDealTroubleshootingFeedback: Not implemented. Ensure you are inside the DealContext');
  },
  useDealTroubleshootingAgentSessionList: () => {
    throw new Error('useDealTroubleshootingAgentSessionList: Not implemented. Ensure you are inside the DealContext');
  },
  useDealTroubleshootingAgentSessionHistory: () => {
    throw new Error(
      'useDealTroubleshootingAgentSessionHistory: Not implemented. Ensure you are inside the DealContext'
    );
  },
  useDealTroubleshootingAgentSendMessage: () => {
    throw new Error('useDealTroubleshootingAgentSendMessage: Not implemented. Ensure you are inside the DealContext');
  },
  useDealTroubleshootingAgentMessageMutationState: () => {
    throw new Error(
      'useDealTroubleshootingAgentMessageMutationState: Not implemented. Ensure you are inside the DealContext'
    );
  },
  useRefreshAuthToken: () => {
    throw new Error('useRefreshAuthToken: Not implemented. Ensure you are inside the DealContext');
  },
  useGetDealInsightsJobId: () => {
    throw new Error('useGetDealInsightsJobId: Not implemented. Ensure you are inside the DealContext');
  },
  useGetForecastJobId: () => {
    throw new Error('useGetForecastJobId: Not implemented. Ensure you are inside the DealContext');
  },
  useInitialValues: () => {
    throw new Error('useInitialValues not implemented.');
  },
  useInitialValuesLazy: () => {
    throw new Error('useInitialValues not implemented.');
  },
  useLanguagesFetch: () => {
    throw new Error('useLanguagesFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useMarketBrandGroupsFetch: () => {
    throw new Error('useMarketBrandGroupsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useMonetizingFetch: () => {
    throw new Error('useMonetizingFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useMultipleDealsFetch: () => {
    throw new Error('useMultipleDealsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useOnBehalfOfAccountId: () => {
    throw new Error('useOnBehalfOfAccountId: Not implemented. Ensure you are inside the DealContext');
  },
  useOnBehalfOfFetch: () => {
    throw new Error('useOnBehalfOfFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useOpenAuctionsAccessFetch: () => {
    throw new Error('useOpenAuctionsAccessFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useOpenAuctionsFetch: () => {
    throw new Error('useOpenAuctionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  usePackageFetch: () => {
    throw new Error('usePackageFetch: Not implemented. Ensure you are inside the DealContext');
  },
  usePackageLazyFetch: () => {
    throw new Error('usePackageLazyFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useParticipantFunctions: () => {
    throw new Error('useParticipantFunctions: Not implemented. Ensure you are inside the DealContext');
  },
  useParticipantHooks: () => {
    throw new Error('useParticipantHooks: Not implemented. Ensure you are inside the DealContext');
  },
  usePmpDealTypeFetch: () => {
    throw new Error('usePmpDealTypeFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useRelatedAccountsFetch: () => {
    throw new Error('useRelatedAccountsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useSetMultiproviderEnabledDataPartner: () => {
    throw new Error('useSetMultiproviderEnabledDataPartner: Not implemented. Ensure you are inside the DealContext');
  },
  useThirdPartyPartnersFetch: () => {
    throw new Error('useThirdPartyPartnersFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useDirectDealsPartnersFetch: () => {
    throw new Error('useDirectDealsPartnersFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useTroubleshootingFetch: () => {
    throw new Error('useTroubleshootingFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useValidationSchema: () => {
    throw new Error('useValidationSchema: Not implemented. Ensure you are inside the DealContext');
  },
  useOrganizationsByIdsFetch: () => {
    throw new Error('useOrganizationsByIdsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  usePdpSyncStatusOptionsFetch: () => {
    throw new Error('usePdpSyncStatusOptionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  usePdpSyncDealStatusOptionsFetch: () => {
    throw new Error('usePdpSyncDealStatusOptionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  usePdpSyncNegotiationStatusOptionsFetch: () => {
    throw new Error('usePdpSyncNegotiationStatusOptionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useAmazonSyncStatusOptionsFetch: () => {
    throw new Error('useAmazonSyncStatusOptionsFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useFeePublisherPmpFetch: () => {
    throw new Error('"useFeePublisherPmpFetch": Not implemented. Ensure you are inside the "DealContext"');
  },
  usePostAuctionDiscountsFetch: () => {
    throw new Error('"usePostAuctionDiscountsFetch": Not implemented. Ensure you are inside the "DealContext"');
  },
  useRebatesFetch: () => {
    throw new Error('"useRebatesFetch": Not implemented. Ensure you are inside the "DealContext"');
  },
  useCanEditSegmentPartnerPlatformShare: () => {
    throw new Error('useCanEditSegmentPartnerPlatformShare: Not implemented. Ensure you are inside the DealContext');
  },
  useDealRevenueBreakdownFetch: () => {
    throw new Error('useDealRevenueBreakdownFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useGlobalFeePublisherPmpFetch: () => {
    throw new Error('useGlobalFeePublisherPmpFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useTpfBoostedBidPriceFetch: () => {
    throw new Error('useTpfBoostedBidPriceFetch: Not implemented. Ensure you are inside the DealContext');
  },
  useBuyerOrganizationsFetch: () => {
    throw new Error('useBuyerOrganizationsFetch: Not implemented. Ensure you are inside the DealContext');
  },
});

export const useDealContext = () => useContext(dealContext);

export function DealProvider({ children, dealConfig }: DealProviderProps) {
  const [selectedOnBehalfOrg, setSelectedOnBehalfOrg] = useState<OrganizationOptionData | null>(null);

  const value: DealContext = useMemo(
    () => ({
      ...dealConfig,
      onBehalfOrganization: selectedOnBehalfOrg,
      setOnBehalfOrganization: setSelectedOnBehalfOrg,
    }),
    [dealConfig, selectedOnBehalfOrg]
  );

  return <dealContext.Provider value={value}>{children}</dealContext.Provider>;
}
